'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Sparkles,
  Gem,
  Tag,
} from 'lucide-react';
import { cargarCatalogoPlanes } from '@/components/hooks/usePlanes';
import {
  cargarPromocionActiva,
  formatearTextoPromocion,
  type PromocionActiva,
} from '@/components/hooks/usePromocion';
import HeadSEO from '@/components/seo/HeadSEO';
import Footer from '@/components/pages/shared/Footer';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Servicio {
  servicio_saas: string;
  descripcion_corta: string | null;
  orden_servicio: number;
  orden_categoria: number;
  categoria: string;
}

interface Plan {
  id_plan: string;
  plan: string;
  orden_plan: number;
  precio_mensual: number;
  precio_trabajador_adicional: number;
  tiene_promocion: boolean;
  promocion_activa: boolean;
  precio_promocion: number | null;
  fecha_fin_promocion: string | null;
  es_gratis: boolean;
  servicios: Servicio[];
}

interface Categoria {
  nombre: string;
  orden: number;
  servicios: Servicio[];
}

// ─── Animation variants ───────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.12, duration: 0.5 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPrecioFinal(plan: Plan): number {
  const fechaFin = plan.fecha_fin_promocion
    ? new Date(plan.fecha_fin_promocion)
    : null;
  const promoValida =
    plan.promocion_activa === true &&
    typeof plan.precio_promocion === 'number' &&
    plan.precio_promocion < plan.precio_mensual &&
    fechaFin instanceof Date &&
    new Date() <= fechaFin;
  return promoValida ? (plan.precio_promocion as number) : plan.precio_mensual;
}

function hasPromo(plan: Plan): boolean {
  const fechaFin = plan.fecha_fin_promocion
    ? new Date(plan.fecha_fin_promocion)
    : null;
  return (
    plan.promocion_activa === true &&
    typeof plan.precio_promocion === 'number' &&
    plan.precio_promocion < plan.precio_mensual &&
    fechaFin instanceof Date &&
    new Date() <= fechaFin
  );
}

function buildCategorias(planes: Plan[]): Categoria[] {
  const map = new Map<string, Categoria>();

  planes.forEach((plan) => {
    plan.servicios.forEach((s) => {
      if (!map.has(s.categoria)) {
        map.set(s.categoria, {
          nombre: s.categoria,
          orden: s.orden_categoria,
          servicios: [],
        });
      }
      const cat = map.get(s.categoria)!;
      if (!cat.servicios.find((x) => x.servicio_saas === s.servicio_saas)) {
        cat.servicios.push(s);
      }
    });
  });

  return Array.from(map.values())
    .sort((a, b) => a.orden - b.orden)
    .map((cat) => ({
      ...cat,
      servicios: cat.servicios.sort(
        (a, b) => a.orden_servicio - b.orden_servicio
      ),
    }));
}

function planIncluye(plan: Plan, servicio_saas: string): boolean {
  return plan.servicios.some((s) => s.servicio_saas === servicio_saas);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Pricing() {
  const navigate = useNavigate();
  const [planes, setPlanes] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [promo, setPromo] = useState<PromocionActiva | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    cargarCatalogoPlanes().then((p: Plan[]) => {
      setPlanes(p);
      setLoading(false);
    });
    cargarPromocionActiva().then(setPromo);
  }, []);

  const categorias = buildCategorias(planes);
  const textoPromo = formatearTextoPromocion(promo);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="flex flex-col min-h-screen bg-white dark:bg-black text-gray-800 dark:text-gray-200"
    >
      <HeadSEO
        title="Precios y planes — Bookoll"
        description="Compara los planes de Bookoll. Sin permanencia."
        canonical="https://bookoll.com/pricing"
      />

      {/* ══════════════════════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden text-white w-full">
        {/* Fondo modo claro */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse at 15% 60%, rgba(40,142,127,0.22) 0%, transparent 50%),
              radial-gradient(ellipse at 85% 20%, rgba(16,50,84,0.5) 0%, transparent 55%),
              linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)
            `,
          }}
        />
        {/* Fondo modo oscuro: negro con resplandor teal, igual que Login */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 55%, rgba(40, 142, 127, 0.35) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 15%, rgba(16, 50, 84, 0.45) 0%, transparent 55%),
              linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
            `,
          }}
        />

        <motion.div
          className="absolute top-10 left-[5%] w-56 h-56 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(40,142,127,0.18) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-4 right-[8%] w-64 h-64 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.75, 0.4] }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />

        <button
          onClick={() => navigate(-1)}
          aria-label="Volver atrás"
          className="absolute top-5 left-5 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline font-medium">Volver</span>
        </button>

        <div className="relative z-10 pt-20 pb-28 px-6 flex flex-col items-center text-center max-w-3xl mx-auto">
          <motion.div
            className="mb-5 w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Sparkles className="w-7 h-7 text-white" />
          </motion.div>

          <motion.h1
            className="text-4xl md:text-5xl font-bold tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          >
            Planes y precios
          </motion.h1>

          <motion.p
            className="mt-4 text-base text-white/70 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
          >
            Sin permanencia. Cancela cuando quieras.
          </motion.p>

          {textoPromo && (
            <motion.div
              className="mt-8 inline-flex items-center gap-2 bg-[#288E7F]/80 backdrop-blur-sm border border-[#288E7F] rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <Gem className="w-4 h-4 text-emerald-300" />
              {textoPromo}
            </motion.div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
          <svg
            viewBox="0 0 1440 60"
            preserveAspectRatio="none"
            className="w-full h-10 sm:h-14"
          >
            <path
              d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z"
              className="fill-white dark:fill-black"
            />
          </svg>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          AVISO DE PROMOCIÓN (se oculta entero si no hay ninguna activa)
      ══════════════════════════════════════════════════════════════════════ */}
      {promo && (
        <section className="w-full bg-white dark:bg-black pt-14 pb-2 px-6">
          <motion.div variants={itemVariants} className="max-w-[92rem] mx-auto">
            {/* Etiqueta de sección */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-5 rounded-full bg-[#288E7F]" />
              <span className="text-xs font-bold text-[#288E7F] uppercase tracking-widest">
                {promo.tipo === 'trial' ? 'Prueba gratuita' : 'Período de lanzamiento'}
              </span>
            </div>

            <div className="rounded-2xl border border-[#288E7F]/30 dark:border-[#288E7F]/25 bg-gradient-to-br from-emerald-50 to-teal-50/40 dark:from-[#288E7F]/10 dark:to-[#1c1c1e] px-6 py-5 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-[#288E7F]/10 dark:bg-[#288E7F]/20 flex items-center justify-center shrink-0">
                <Gem className="w-6 h-6 text-[#288E7F]" />
              </div>
              <div>
                <p className="font-semibold text-[#103254] dark:text-white text-base">
                  Conviértete en Negocio Fundador
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Sé uno de los primeros negocios en unirte y consigue tu
                  suscripción gratuita de por vida.
                </p>
                <p className="text-xs font-medium text-[#288E7F] mt-2">
                  Solo para los primeros negocios. Plazas limitadas.
                </p>
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          COMPARATIVA DE PLANES
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-14 px-4 sm:px-8 w-full max-w-[92rem] mx-auto bg-[#288E7F]/[0.02] dark:bg-white/[0.02] rounded-3xl">
        {/* Encabezado de sección */}
        <motion.div variants={itemVariants} className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-5 rounded-full bg-[#103254] dark:bg-white" />
            <span className="text-xs font-bold text-[#103254] dark:text-white uppercase tracking-widest">
              Comparativa de planes
            </span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white ml-4 pl-0.5">
            Elige el plan que mejor se adapta a tu negocio
          </h2>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1 ml-4 pl-0.5">
            {promo
              ? 'Todos los planes incluyen acceso completo durante la promoción activa.'
              : 'Todos los planes incluyen acceso completo desde el primer día.'}
          </p>
          <div className="mt-5 h-px bg-gradient-to-r from-[#103254]/20 dark:from-white/20 via-gray-200 dark:via-white/10 to-transparent" />
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#103254] dark:border-t-white dark:border-b-white/20" />
          </div>
        ) : planes.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500 text-center py-10">
            No hay planes disponibles.
          </p>
        ) : (
          <>
            {/* ──────────────────────────────────────────────────────────────
                TARJETAS MÓVIL (< md)
            ────────────────────────────────────────────────────────────── */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col gap-6 md:hidden"
            >
              {planes.map((plan, idx) => {
                const precio = getPrecioFinal(plan);
                const promoPlan = hasPromo(plan);
                const isDestacado = idx === 1;

                return (
                  <div
                    key={plan.id_plan}
                    className={`relative rounded-2xl border ${
                      isDestacado
                        ? 'border-[#103254]/20 dark:border-white/20 shadow-lg ring-1 ring-[#103254]/10 dark:ring-white/10'
                        : 'border-gray-100 dark:border-white/10 shadow-sm'
                    } bg-white dark:bg-[#1c1c1e] overflow-hidden`}
                  >
                    {/* Franja de color superior */}
                    <div
                      className={`h-1 w-full ${
                        isDestacado
                          ? 'bg-gradient-to-r from-[#103254] to-[#288E7F]'
                          : 'bg-gradient-to-r from-gray-200 dark:from-white/10 to-gray-100 dark:to-white/5'
                      }`}
                    />

                    {/* Cabecera */}
                    <div className="px-6 pt-5 pb-5 bg-gray-50/60 dark:bg-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                          {plan.plan}
                        </span>
                        {isDestacado && (
                          <span className="text-[10px] font-semibold text-[#103254]/70 dark:text-white bg-[#103254]/8 dark:bg-white/10 border border-[#103254]/15 dark:border-white/20 px-2 py-0.5 rounded-full">
                            Más popular
                          </span>
                        )}
                      </div>

                      <div className="flex items-baseline gap-1">
                        {promoPlan && (
                          <span className="text-sm font-medium text-gray-300 dark:text-gray-600 line-through mr-1">
                            {plan.precio_mensual.toFixed(2)}€
                          </span>
                        )}
                        <span className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                          {precio.toFixed(2)}€
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">/mes</span>
                      </div>

                      {promoPlan && plan.fecha_fin_promocion && (
                        <span className="mt-2 inline-flex items-center gap-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold px-2.5 py-0.5 rounded-full">
                          <Tag className="w-2.5 h-2.5" />
                          Promo hasta{' '}
                          {new Date(
                            plan.fecha_fin_promocion
                          ).toLocaleDateString('es-ES')}
                        </span>
                      )}

                      {plan.precio_trabajador_adicional > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200/80 dark:border-white/10 flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Por trabajador adicional
                          </span>
                          <span className="text-sm font-semibold text-[#103254] dark:text-white">
                            {plan.precio_trabajador_adicional.toFixed(2)}€
                            <span className="text-xs font-normal text-gray-400 dark:text-gray-500 ml-0.5">
                              /mes
                            </span>
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Separador con etiqueta */}
                    <div className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-[#1c1c1e] border-t border-gray-100 dark:border-white/10">
                      <span className="text-[9px] font-bold text-gray-300 dark:text-gray-600 uppercase tracking-widest">
                        Funcionalidades incluidas
                      </span>
                      <span className="h-px flex-1 bg-gray-100 dark:bg-white/10" />
                    </div>

                    {/* Servicios agrupados por categoría */}
                    <div className="divide-y divide-gray-50 dark:divide-white/5">
                      {categorias.map((cat) => (
                        <div key={cat.nombre}>
                          {/* Cabecera de categoría más marcada */}
                          <div className="px-6 py-2.5 bg-[#288E7F]/[0.15] dark:bg-[#288E7F]/[0.12] border-l-2 border-[#288E7F]/70 flex items-center gap-2">
                            <span className="text-[10px] font-bold text-[#288E7F] uppercase tracking-widest">
                              {cat.nombre}
                            </span>
                          </div>
                          <ul className="px-6 py-3 space-y-2.5">
                            {cat.servicios.map((servicio) => {
                              const included = planIncluye(
                                plan,
                                servicio.servicio_saas
                              );
                              return (
                                <li
                                  key={servicio.servicio_saas}
                                  className="flex items-start gap-3"
                                >
                                  {included ? (
                                    <CheckCircle className="w-4 h-4 text-[#288E7F] shrink-0 mt-0.5" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-gray-200 dark:text-gray-700 shrink-0 mt-0.5" />
                                  )}
                                  <div>
                                    <span
                                      className={`text-sm ${
                                        included
                                          ? 'text-gray-700 dark:text-gray-200'
                                          : 'text-gray-300 dark:text-gray-600'
                                      }`}
                                    >
                                      {servicio.servicio_saas}
                                    </span>
                                    {included && servicio.descripcion_corta && (
                                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">
                                        {servicio.descripcion_corta}
                                      </p>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </motion.div>

            {/* ──────────────────────────────────────────────────────────────
                TABLA DESKTOP (≥ md)
            ────────────────────────────────────────────────────────────── */}
            <motion.div
              variants={itemVariants}
              className="hidden md:block overflow-x-auto rounded-2xl border border-gray-200 dark:border-white/10 shadow-md"
            >
              <table className="w-full text-sm border-collapse">
                <thead>
                  {/* Franja de acento superior en la cabecera */}
                  <tr>
                    <td className="bg-gray-50 dark:bg-[#1c1c1e] rounded-tl-2xl p-0 border-b-0" />
                    {planes.map((plan, idx) => (
                      <td
                        key={plan.id_plan}
                        className={`p-0 border-b-0 ${
                          idx === planes.length - 1 ? 'rounded-tr-2xl' : ''
                        }`}
                      >
                        <div
                          className={`h-1 w-full ${
                            idx === 1
                              ? 'bg-gradient-to-r from-[#103254] to-[#288E7F]'
                              : 'bg-transparent'
                          }`}
                        />
                      </td>
                    ))}
                  </tr>

                  <tr className="border-b-2 border-gray-100 dark:border-white/10">
                    <th className="bg-gray-50 dark:bg-[#1c1c1e] px-8 py-6 text-left w-2/5">
                      <span className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                        Qué incluye cada plan
                      </span>
                    </th>
                    {planes.map((plan, idx) => {
                      const precio = getPrecioFinal(plan);
                      const promoPlan = hasPromo(plan);
                      const isDestacado = idx === 1;

                      return (
                        <th
                          key={plan.id_plan}
                          className={`px-8 py-6 text-center bg-gray-50 dark:bg-[#1c1c1e] ${
                            isDestacado ? 'bg-[#103254]/[0.03] dark:bg-white/[0.04]' : ''
                          }`}
                        >
                          <div className="flex flex-col items-center gap-0.5">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                {plan.plan}
                              </span>
                              {isDestacado && (
                                <span className="text-[10px] font-semibold text-[#103254]/70 dark:text-white bg-[#103254]/8 dark:bg-white/10 border border-[#103254]/15 dark:border-white/20 px-2 py-0.5 rounded-full leading-none">
                                  Más popular
                                </span>
                              )}
                            </div>
                            <div className="flex items-baseline gap-1 mt-2">
                              {promoPlan && (
                                <span className="text-sm font-medium text-gray-300 dark:text-gray-600 line-through mr-1">
                                  {plan.precio_mensual.toFixed(2)}€
                                </span>
                              )}
                              <span className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                                {precio.toFixed(2)}€
                              </span>
                              <span className="text-xs text-gray-400 dark:text-gray-500 self-end pb-0.5">
                                /mes
                              </span>
                            </div>
                            {promoPlan && plan.fecha_fin_promocion && (
                              <span className="mt-1.5 inline-flex items-center gap-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold px-2.5 py-0.5 rounded-full">
                                <Tag className="w-2.5 h-2.5" />
                                Promo hasta{' '}
                                {new Date(
                                  plan.fecha_fin_promocion
                                ).toLocaleDateString('es-ES')}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {/* ── Fila: trabajador adicional ── */}
                  <tr className="border-b-2 border-gray-100 dark:border-white/10 bg-[#288E7F]/[0.05] dark:bg-[#288E7F]/[0.08] hover:bg-[#288E7F]/10 dark:hover:bg-[#288E7F]/15 transition-colors">
                    <td className="px-8 py-4">
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className="w-1 h-4 rounded-full bg-[#288E7F]/50 shrink-0" />
                        <span className="font-semibold text-gray-700 dark:text-gray-200">
                          Trabajador adicional
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 block pl-3">
                        Añade más profesionales a tu plan cuando lo necesites
                      </span>
                    </td>
                    {planes.map((plan) => (
                      <td key={plan.id_plan} className="px-8 py-4 text-center">
                        {plan.precio_trabajador_adicional > 0 ? (
                          <span className="font-bold text-[#103254] dark:text-white text-base">
                            {plan.precio_trabajador_adicional.toFixed(2)}€
                            <span className="text-xs font-normal text-gray-400 dark:text-gray-500 ml-0.5">
                              /mes
                            </span>
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* ── Categorías y servicios ── */}
                  {categorias.map((cat) => (
                    <>
                      {/* Fila de sección / categoría — más prominente */}
                      <tr
                        key={`cat-${cat.nombre}`}
                        className="border-b border-gray-100 dark:border-white/10"
                      >
                        <td colSpan={planes.length + 1} className="px-0 py-0">
                          <div className="flex items-center gap-0 border-t-2 border-b border-[#288E7F]/25">
                            {/* Acento lateral izquierdo */}
                            <div className="w-1 self-stretch bg-[#288E7F]/70 shrink-0" />
                            <div className="px-7 py-3 bg-gradient-to-r from-[#288E7F]/[0.18] dark:from-[#288E7F]/[0.14] to-[#288E7F]/[0.06] dark:to-[#288E7F]/[0.04] flex items-center gap-2 flex-1">
                              <span className="text-[11px] font-bold text-[#288E7F] uppercase tracking-[0.15em]">
                                {cat.nombre}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Filas de servicios */}
                      {cat.servicios.map((servicio) => (
                        <tr
                          key={servicio.servicio_saas}
                          className="border-b border-gray-50 dark:border-white/5 transition-colors hover:bg-gray-50/50 dark:hover:bg-white/5"
                        >
                          <td className="px-8 py-4">
                            <span className="font-medium text-gray-700 dark:text-gray-200 block">
                              {servicio.servicio_saas}
                            </span>
                            {servicio.descripcion_corta && (
                              <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 block leading-snug">
                                {servicio.descripcion_corta}
                              </span>
                            )}
                          </td>
                          {planes.map((plan) => {
                            const included = planIncluye(
                              plan,
                              servicio.servicio_saas
                            );
                            return (
                              <td
                                key={plan.id_plan}
                                className="px-8 py-4 text-center"
                              >
                                {included ? (
                                  <CheckCircle className="w-5 h-5 text-[#288E7F] mx-auto" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-gray-200 dark:text-gray-700 mx-auto" />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </>
                  ))}

                  {/* Fila de cierre */}
                  <tr className="border-t-2 border-gray-100 dark:border-white/10">
                    <td className="px-8 py-5 bg-gray-50 dark:bg-[#1c1c1e] rounded-bl-2xl" />
                    {planes.map((plan, idx) => (
                      <td
                        key={plan.id_plan}
                        className={`px-8 py-5 bg-gray-50 dark:bg-[#1c1c1e] ${
                          idx === planes.length - 1 ? 'rounded-br-2xl' : ''
                        }`}
                      />
                    ))}
                  </tr>
                </tbody>
              </table>
            </motion.div>
          </>
        )}
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          NOTA FINAL / CONTACTO
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="pb-16 px-6 bg-white dark:bg-black">
        {/* Divisor con etiqueta */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-200 dark:to-white/10" />
            <span className="text-[10px] font-bold text-gray-300 dark:text-gray-600 uppercase tracking-widest">
              ¿Alguna duda?
            </span>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-200 dark:to-white/10" />
          </div>
        </div>

        <p className="text-sm text-gray-400 dark:text-gray-500 max-w-xl mx-auto text-center">
          ¿Tienes dudas sobre qué plan elegir?{' '}
          <a
            href="mailto:soporte@bookoll.com"
            className="text-[#103254] dark:text-[#4fb8a3] font-semibold hover:opacity-80 underline underline-offset-2"
          >
            Escríbenos
          </a>{' '}
          y te ayudamos a encontrar la mejor opción para tu negocio.
        </p>
      </section>

      <Footer />
    </motion.div>
  );
}